import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timezones = [
  "UTC", "America/New_York", "America/Los_Angeles", "America/Chicago",
  "Europe/London", "Europe/Paris", "Asia/Tokyo", "Asia/Shanghai",
  "Asia/Kolkata", "Australia/Sydney"
];

export default function TimestampConverter() {
  const navigate = useNavigate();
  const [currentTimestamp, setCurrentTimestamp] = useState(Math.floor(Date.now() / 1000));
  const [timestamp, setTimestamp] = useState("");
  const [dateString, setDateString] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [convertResult, setConvertResult] = useState(null);
  
  // Batch conversion
  const [batchInput, setBatchInput] = useState("");
  const [batchResults, setBatchResults] = useState([]);
  
  // Time difference
  const [timestamp1, setTimestamp1] = useState("");
  const [timestamp2, setTimestamp2] = useState("");
  const [diffResult, setDiffResult] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimestamp(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleConvert = async () => {
    try {
      const payload = timestamp ? 
        { timestamp: parseInt(timestamp), timezone } :
        { date_string: dateString, timezone };
      
      const response = await axios.post(`${API}/timestamp/convert`, payload);
      setConvertResult(response.data);
      toast.success("Converted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Conversion failed");
    }
  };

  const handleBatchConvert = async () => {
    try {
      const timestamps = batchInput.split('\n')
        .map(line => line.trim())
        .filter(line => line && !isNaN(line))
        .map(line => parseInt(line));
      
      if (timestamps.length === 0) {
        toast.error("Please enter valid timestamps (one per line)");
        return;
      }
      
      const response = await axios.post(`${API}/timestamp/batch`, {
        timestamps,
        timezone
      });
      setBatchResults(response.data.results);
      toast.success(`Converted ${timestamps.length} timestamps!`);
    } catch (error) {
      toast.error("Batch conversion failed");
    }
  };

  const handleCalculateDifference = async () => {
    try {
      const response = await axios.post(`${API}/timestamp/difference`, {
        timestamp1: parseInt(timestamp1),
        timestamp2: parseInt(timestamp2)
      });
      setDiffResult(response.data);
      toast.success("Time difference calculated!");
    } catch (error) {
      toast.error("Calculation failed");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-home-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Unix Timestamp Converter</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Current Timestamp */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white mb-2" style={{fontFamily: 'Space Grotesk'}}>Current Unix Timestamp</h2>
              <div className="flex items-center space-x-4">
                <div className="text-4xl font-bold text-cyan-400 font-mono">{currentTimestamp}</div>
                <Button
                  data-testid="copy-current-btn"
                  onClick={() => copyToClipboard(currentTimestamp.toString())}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-zinc-500 mt-2">{new Date().toUTCString()}</p>
            </div>
            <Clock className="w-16 h-16 text-zinc-700" />
          </div>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 p-6">
          <Tabs defaultValue="convert" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 bg-zinc-800 border-zinc-700 mb-6">
              <TabsTrigger value="convert" data-testid="convert-tab">Convert</TabsTrigger>
              <TabsTrigger value="batch" data-testid="batch-tab">Batch</TabsTrigger>
              <TabsTrigger value="difference" data-testid="difference-tab">Time Difference</TabsTrigger>
            </TabsList>

            {/* Convert Tab */}
            <TabsContent value="convert" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="timezone" className="text-zinc-400 mb-2 block">Timezone</Label>
                  <select
                    data-testid="timezone-select"
                    id="timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                  >
                    {timezones.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="timestamp" className="text-zinc-400 mb-2 block">Unix Timestamp</Label>
                  <Input
                    data-testid="timestamp-input"
                    id="timestamp"
                    value={timestamp}
                    onChange={(e) => {
                      setTimestamp(e.target.value);
                      setDateString("");
                    }}
                    placeholder="1704067200"
                    className="bg-zinc-800 border-zinc-700 text-white font-mono"
                  />
                </div>
                <div>
                  <Label htmlFor="date" className="text-zinc-400 mb-2 block">Or Date String</Label>
                  <Input
                    data-testid="date-input"
                    id="date"
                    value={dateString}
                    onChange={(e) => {
                      setDateString(e.target.value);
                      setTimestamp("");
                    }}
                    placeholder="2024-01-01 00:00:00"
                    className="bg-zinc-800 border-zinc-700 text-white font-mono"
                  />
                </div>
              </div>

              <Button
                data-testid="convert-btn"
                onClick={handleConvert}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              >
                Convert
              </Button>

              {convertResult && (
                <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                  <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Result</h3>
                  <div className="space-y-3">
                    {convertResult.timestamp && (
                      <div className="flex justify-between items-center py-2 border-b border-zinc-700">
                        <span className="text-zinc-400">Unix Timestamp:</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-mono">{convertResult.timestamp}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(convertResult.timestamp.toString())}
                            className="h-8 w-8 p-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2 border-b border-zinc-700">
                      <span className="text-zinc-400">UTC:</span>
                      <span className="text-white font-mono">{convertResult.utc}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-zinc-700">
                      <span className="text-zinc-400">Local ({timezone}):</span>
                      <span className="text-white font-mono">{convertResult.local}</span>
                    </div>
                    {convertResult.human && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-zinc-400">Human Readable:</span>
                        <span className="text-white">{convertResult.human}</span>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* Batch Tab */}
            <TabsContent value="batch" className="space-y-6">
              <div>
                <Label htmlFor="batch-input" className="text-zinc-400 mb-2 block">Timestamps (one per line)</Label>
                <Textarea
                  data-testid="batch-input"
                  id="batch-input"
                  value={batchInput}
                  onChange={(e) => setBatchInput(e.target.value)}
                  placeholder={`1704067200\n1704153600\n1704240000`}
                  className="bg-zinc-800 border-zinc-700 text-white font-mono min-h-[200px]"
                />
              </div>

              <Button
                data-testid="batch-convert-btn"
                onClick={handleBatchConvert}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              >
                Convert Batch
              </Button>

              {batchResults.length > 0 && (
                <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                  <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Results ({batchResults.length})</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {batchResults.map((result, index) => (
                      <div key={index} className="bg-zinc-900/50 p-3 rounded border border-zinc-700">
                        <div className="text-sm text-zinc-400">Timestamp: <span className="text-white font-mono">{result.timestamp}</span></div>
                        <div className="text-sm text-zinc-400">UTC: <span className="text-white">{result.utc}</span></div>
                        <div className="text-sm text-zinc-400">Local: <span className="text-white">{result.local}</span></div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* Time Difference Tab */}
            <TabsContent value="difference" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="ts1" className="text-zinc-400 mb-2 block">First Timestamp</Label>
                  <Input
                    data-testid="timestamp1-input"
                    id="ts1"
                    value={timestamp1}
                    onChange={(e) => setTimestamp1(e.target.value)}
                    placeholder="1704067200"
                    className="bg-zinc-800 border-zinc-700 text-white font-mono"
                  />
                </div>
                <div>
                  <Label htmlFor="ts2" className="text-zinc-400 mb-2 block">Second Timestamp</Label>
                  <Input
                    data-testid="timestamp2-input"
                    id="ts2"
                    value={timestamp2}
                    onChange={(e) => setTimestamp2(e.target.value)}
                    placeholder="1704153600"
                    className="bg-zinc-800 border-zinc-700 text-white font-mono"
                  />
                </div>
              </div>

              <Button
                data-testid="calculate-diff-btn"
                onClick={handleCalculateDifference}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              >
                Calculate Difference
              </Button>

              {diffResult && (
                <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                  <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Time Difference</h3>
                  <div className="space-y-4">
                    <div className="text-center py-6 bg-zinc-900/50 rounded-lg border border-zinc-700">
                      <div className="text-4xl font-bold text-cyan-400 mb-2">{diffResult.difference_readable}</div>
                      <div className="text-sm text-zinc-500">Total: {diffResult.difference_seconds.toLocaleString()} seconds</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                        <div className="text-sm text-zinc-400">Days</div>
                        <div className="text-2xl font-bold text-white">{diffResult.days}</div>
                      </div>
                      <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                        <div className="text-sm text-zinc-400">Total Hours</div>
                        <div className="text-2xl font-bold text-white">{diffResult.hours}</div>
                      </div>
                    </div>
                    <div className="text-sm text-zinc-400">
                      <div>From: <span className="text-white">{diffResult.from}</span></div>
                      <div>To: <span className="text-white">{diffResult.to}</span></div>
                    </div>
                  </div>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}